import { useTranslation } from "react-i18next";
import { getErrorMessage, Mode, QuestionField } from "../Form";
import { MultiSelection } from "../MultiSelection/MultiSelection";
import { SingleSelection } from "../SingleSelection/SingleSelection";
import { useEffect, useRef, useState } from "react";
import { RadioButtonGroup } from "../../RadioButtonGroup/RadioButtonGroup";
import HTMLParser from "../../HTMLParser/HTMLParser";
import Selector from "../../Selector/Selector";
import RadioButton from "../../RadioButton/RadioButton";
import Field from "../../Field/Field";
import { uniqueID } from "../../../../util";
import { TrashIcon } from "../../../svgs";
import BtButton from "../../../../ts-components/bt-button/bt-button";
import styles from "./SelectionComponent.module.scss";
import { MultiValue, SingleValue } from "react-select";
import { CheckboxGroup } from "../../CheckboxGroup/CheckboxGroup";
import StyledCheckbox from "../../StyledCheckbox/StyledCheckbox";

type Option = {
  label: string;
  value: string;
  selected: boolean;
}

type Props<T extends SingleSelection | MultiSelection> = {
  element: T,
  mode: Mode,
  onElementChange: (element: T) => void,
  selectionMode: 'single' | 'multi',
};
export const SelectionComponent = <T extends SingleSelection | MultiSelection>({ element, mode, onElementChange, selectionMode }: Props<T>) => {
  const { question, options, required, questionErrorMessage, answerErrorMessage } = element;

  const [t] = useTranslation('shared', { keyPrefix: 'form' });
  
  const translatedAnswerErrorMessage = getErrorMessage(t, answerErrorMessage);
  const translatedQuestionErrorMessage = getErrorMessage(t, questionErrorMessage);

  const lastOptionRef = useRef<HTMLInputElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (loaded && lastOptionRef.current && options.length > 1) {
      lastOptionRef.current.focus();
    }
  }, [lastOptionRef, options.length]);

  if (mode === Mode.FORM || mode === Mode.READ_ONLY) {
    const isReadOnly = mode === Mode.READ_ONLY;
    if (options.length < 6) {
      if (selectionMode === 'single') {
        return (
          <RadioButtonGroup
            readOnly={isReadOnly}
            label={<HTMLParser data={question} />}
            required={required}
            errorMessage={translatedAnswerErrorMessage}
            options={options}
            onChange={isReadOnly ? undefined : (array) => {
              element.options = array;
              onElementChange(element);
            }}
          />
        )
      } else {
        return (
          <CheckboxGroup
            readOnly={isReadOnly}
            label={<HTMLParser data={question} />}
            required={required}
            errorMessage={translatedAnswerErrorMessage}
            options={options}
            onChange={(array) => {
              if (isReadOnly) {
                return;
              }
              element.options = array;
              onElementChange(element);
            }}
          />
        )
      }
    }

    const onChange = (value: SingleValue<Option> | MultiValue<Option>): void => {
      if (isReadOnly) return;

      if (value instanceof Array) {
        element.options = element.options.map(option => ({
          ...option,
          selected: value.some(x => x.value === option.value),
        }));
      } else {
        element.options = element.options.map(option => ({
          ...option,
          selected: value ? option.value === value.value : false,
        }));
      }

      onElementChange(element);
    }

    const singleValue: SingleValue<Option> = options.find(x => x.selected) ?? null;
    const multiValue: MultiValue<Option>   = options.filter(x => x.selected);
    const value: SingleValue<Option> | MultiValue<Option> =
      selectionMode === "single" ? singleValue : multiValue;
    
    return <Selector<Option, boolean> isMulti={selectionMode === 'multi'} readOnly={isReadOnly} options={options} label={<HTMLParser data={question} />} required={required} errorMessage={translatedAnswerErrorMessage} value={value} onChange={onChange}  />
  } else if (mode === Mode.EDIT) {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <QuestionField
        value={question}
        onChange={(value) => {
          element.question = value;
          onElementChange(element);
        }}
        errorMessage={translatedQuestionErrorMessage}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <span style={{ fontSize: '14px', color: 'var(--gray-700)', fontWeight: 500 }}>{t('options', 'Options')}</span>
        {options.map((option, index) => {
          return <div className={styles.option} key={option.value}>
            {selectionMode === 'single' ? <RadioButton
              style={{ width: 'fit-content', pointerEvents: 'none' }}
              checked={false}
              readonly={true}
            /> : <StyledCheckbox
              style={{ width: 'fit-content', pointerEvents: 'none' }}
              className={styles.checkbox}
              disabled={true}
            />}
            <Field
              ref={index === options.length - 1 ? lastOptionRef : null}
              placeholder={`${t('option', 'Option')} ${index + 1}`}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  element.options.push({ label: '', value: uniqueID()+'', selected: false });
                  onElementChange(element);
                } 
              }}
              className={styles.optionFieldWrapper}
              value={option.label}
              onChange={value => {
                option.label = value;
                onElementChange(element)
              }}
            />
            <div className={styles.removeOptionButton} style={{ cursor: options.length === 1 ? 'default' : 'pointer' }} onClick={() => {
              if (options.length === 1) return;
              element.options.splice(index, 1);
              onElementChange(element);
            }}>
              <TrashIcon size='14px' color={options.length === 1 ? 'var(--gray-300)' : 'var(--gray-700)'} />
            </div>
          </div>
        })}
        <BtButton style={{ width: 'fit-content', alignSelf: 'flex-start', marginTop: '8px' }} color='white' size='xxs' onClick={() => {
          element.options.push({ label: '', value: uniqueID()+'', selected: false });
          onElementChange(element);
        }}>{t(`addOption`, 'Add option')}</BtButton>
      </div>
    </div>
  }
}